import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from "react-router-dom";
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Button } from 'ui/atoms/buttons';
import { ContainerWrapper } from 'components/reusable/containers';
import CreatedPassTable from 'components/reusable/passTable';
import { TemplateBlock, TemplateRendererWrapper } from 'components/reusable/containers';
import styled, { css } from 'styled-components';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, XAxis, YAxis, Bar } from 'recharts';


interface TabProps {
  label: string;
  value: number;
  active?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}


interface PassStatisticsProps {
  retrieveTemplateStatisticsHook: (id: string) => any;
  i18nNamespace: string;
  baseUrl: string;
}

// Styled components
export const DashboardWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  padding: 24px;

  @media (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0px 2px 12px rgba(0,0,0,0.1);
`;

export const Title = styled.h2`
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
`;

const TabButton = styled.button<{ active?: boolean }>`
  padding: 12px 20px;
  font-size: 15px;
  border: none;
  background: none;
  cursor: pointer;
  outline: none;
  border-bottom: 3px solid transparent;
  color: #555;

  ${({ active }) =>
    active &&
    css`
      color: #111;
      border-bottom-color: #1976d2;
      font-weight: 600;
    `}

  &:hover {
    background-color: #f7f7f7;
  }
`;

export const Tab: React.FC<TabProps> = ({ label, active, onClick }) => {
  return (
    <TabButton role="tab" active={active} onClick={onClick}>
      {label}
    </TabButton>
  );
};

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px;

  button {
    margin: 10px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5); /* Tinted background */
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* Ensure it appears above other content */
`;

const ModalContent = styled.div`
  background: white; /* Solid background */
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1001; /* Ensure it appears above the overlay */
`;

const ModalButton = styled(Button)`
  margin: 10px;
`;

interface TemplateDetailsProps {
  TemplateRenderer: any;
  retrieveHook: (id: string) => any;
  retrieveStatisticsHook: (id: string) => any;
  deletePassHook: () => any;
  deleteTemplateHook: () => any;
  baseUrl: string;
  i18nNamespace: string;
};

const TabPanel = (props: { children?: React.ReactNode; index: number; value: number }) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div>{children}</div>
      )}
    </div>
  );
}

const TemplateStatistics = ({ retrieveTemplateStatisticsHook, i18nNamespace }: PassStatisticsProps) => {
  const { t } = useTranslation(i18nNamespace);
  const { id } = useParams();
  const { data, isLoading } = retrieveTemplateStatisticsHook(String(id));

  const COLORS = ["#4caf50", "#e0e0e0"];

  if(isLoading) {
    return(<div>{t('Loading')}</div>)
  } else {
    if (!data || data.total_passes === 0) {
      return <div>{t('noStatisticsAvailable')}</div>;
    }
    const pieData = [
      { name: "Active Passes", value: data.active_passes },
      { name: "Other Passes", value: data.total_passes - data.active_passes },
    ];

    const barData = [
      { name: "3-day", value: data["3-day_active_passes"] },
      { name: "7-day", value: data["7-day_active_passes"] },
      { name: "30-day", value: data["30-day_active_passes"] },
    ];
    return (
      <DashboardWrapper>
        
        {/* Pie card */}
        <Card>
          <Title>Total vs Active Passes</Title>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={4}
              >
                {pieData.map((_, index) => (
                  <Cell key={index} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Bar chart card */}
        <Card>
          <Title>Active Passes (Period)</Title>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData}>
              <XAxis dataKey="name" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

      </DashboardWrapper>
    );
  }
}


interface TabsProps {
  value: number;
  onChange: (e: React.MouseEvent, newValue: number) => void;
  children: React.ReactNode;
}

const TabsWrapper = styled.div`
  display: flex;
  border-bottom: 2px solid #ececec;
`;

export const Tabs: React.FC<TabsProps> = ({ value, onChange, children }) => {
  return (
    <TabsWrapper role="tablist">
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) return child;

        // Pass props into <Tab />
        if (child.type === Tab) {
          return React.cloneElement(child, {
            active: child.props.value === value,
            onClick: (e: React.MouseEvent) => onChange(e, child.props.value),
          });
        }

        return child;
      })}
    </TabsWrapper>
  );
};


export const createTemplateDetails = ({TemplateRenderer, retrieveHook, retrieveStatisticsHook, deletePassHook, deleteTemplateHook, baseUrl, i18nNamespace}: TemplateDetailsProps) => {
  const TemplateDetails = () => {

    const { t } = useTranslation(i18nNamespace);
    const { id } = useParams();
    const navigate = useNavigate();
    const { data, isLoading, isSuccess: isRetrieveSuccess } = retrieveHook(String(id));
    const [ deleteCreatedPass, { isSuccess: isDeleteSuccess } ] = deletePassHook();
    const [ deleteTemplate, { isSuccess: isDeleteTemplateSuccess } ] = deleteTemplateHook();
    const [scenarioType, setScenarioType] = useState<string | null>(null);
    const [scenarioId, setScenarioId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false); // Add state for modal
    const [activeTab, setActiveTab] = useState(0);

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);
    const handleConfirmDelete = () => {
      onDeleteTemplate();
      handleCloseModal();
    };

    const deletePass = (passId: string) => {
      deleteCreatedPass({templateId: String(id), passId})
    }

    useEffect(() => {
      if(isDeleteSuccess) {
        window.location.reload();
      }
      if(isRetrieveSuccess) {
        if (Object.keys(data).includes('scenarios') && Object.keys(data.scenarios).length > 0) {
          for (const [key, value] of Object.entries(data.scenarios)) {
            setScenarioType(key);
            setScenarioId(value as string);
          }
        }
      }
    }, [isDeleteSuccess, isRetrieveSuccess])

    const openScenarioDashboard = () => {
      navigate(`scenarios/${scenarioType}/${scenarioId}/`, { state: { passTemplate: data } });
    }

    const onDeleteTemplate = () => {
      deleteTemplate(String(id))
    }

    useEffect(() => {
      if(isDeleteTemplateSuccess) {
        navigate('/backoffice' + baseUrl)
      }
    }, [isDeleteTemplateSuccess])

    if(isLoading) {
      return(<div>{t('loading')}</div>)
    } else {
      return (
        <ContainerWrapper>
          <Grid container>
            <div className="container text-center">
              <div className="row justify-content-start">
                <div className="col-3">
                  <TemplateBlock>
                    <TemplateRendererWrapper>
                      <TemplateRenderer pass={data} />
                    </TemplateRendererWrapper>
                    <TemplateRendererWrapper>
                      <TemplateRenderer.Back pass={data} />
                    </TemplateRendererWrapper>
                    <ButtonWrapper>
                      <Link to='edit/'>
                        <Button>{t('edit')}</Button>
                      </Link>
                      <Button $variant='danger' onClick={handleOpenModal}>{t('delete')}</Button> {/* Update button */}
                    </ButtonWrapper>
                  </TemplateBlock>
                </div>
                <div className="col-9">
                  <ButtonWrapper>
                    <Link to='create/'>
                      <Button $variant='primary'>{t('generateNewAdmin')}</Button>
                    </Link>
                    <Link to={baseUrl + id + '/create/'}>
                      <Button $variant='primary'>{t('generateNewClient')}</Button>
                    </Link>
                    <Button onClick={openScenarioDashboard} hidden={!scenarioType}>{t('scenarioDashboard')}</Button>
                  </ButtonWrapper>
                  <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label={t('statistics')} value={0} />
                    <Tab label={t('createdPasses')} value={1} />
                  </Tabs>
                  <TabPanel value={activeTab} index={0}>
                    <TemplateStatistics retrieveTemplateStatisticsHook={retrieveStatisticsHook} i18nNamespace={i18nNamespace} baseUrl={baseUrl} />
                  </TabPanel>
                  <TabPanel value={activeTab} index={1}>
                    <CreatedPassTable data={data?.created_passes} deletePass={deletePass}></CreatedPassTable>
                  </TabPanel>
                </div>
              </div>
            </div>
          </Grid>
          {isModalOpen && (
            <ModalOverlay>
              <ModalContent>
                <h2>{t('confirmDelete')}</h2>
                <p>{t('areYouSure')}</p>
                <ModalButton $variant='danger' onClick={handleConfirmDelete}>{t('yes')}</ModalButton>
                <ModalButton onClick={handleCloseModal}>{t('no')}</ModalButton>
              </ModalContent>
            </ModalOverlay>
          )}
        </ContainerWrapper>
      );
    }
  }
  return TemplateDetails;
};